import React, { useState } from 'react';
import Button from '../Button';
import { cancelFamilyPortalLesson } from '../../data/familyPortal';
import {
  CANCEL_SIDES, familyPortalCancelNoteMax, familyPortalRefusal, familyPortalWord as w,
} from '../../domain/familyPortal';
import { formatLongDate, formatTime } from '../../domain/locale';
import { zoneAbbreviation } from '../../domain/timeZone';
import p from '../../pages/PortalPage.module.css';
import b from './FindAndBook.module.css';
import s from './CancelBooking.module.css';

// Cancel Booking (#775): MMS's lesson popover → "Cancel Booking" → "Are you
// sure you want to cancel this booking?" with an optional Note to Teacher of
// 255 characters, No and Yes.
//
// Ours also shows the studio's Policy Text and, from the server's own rule
// (functions/portal/cancelPolicy.js, through the view), which side of the
// deadline this is and what the cancel will do — taken off the lesson, or
// marked absent and whether it is still billed. Yes sends back the code of
// what was shown; if the deadline passed while the dialog was open, or the
// teacher changed the policy, the server refuses (`cancel_changed`) and the
// family looks again, rather than getting something they were not told.
//
// Opened in place of the page body, as Booking Details is (FindAndBook.jsx
// says why). No `Date` is built: every time is the server's, on the family's
// clock.

/**
 * The links under one lesson: "Reschedule" (#543) and "Cancel Booking", one
 * of each per student the portal may move or cancel.
 */
export function CancelLinks({
  lesson, view, onCancel, onReschedule = null,
}) {
  const cancels = onCancel ? lesson.studentIds.filter((id) => lesson.cancel?.[id]) : [];
  const moves = onReschedule ? lesson.studentIds.filter((id) => lesson.reschedule?.[id]) : [];
  if (!cancels.length && !moves.length) return null;
  const byId = new Map(view.students.map((x) => [x.id, x]));
  const several = lesson.studentIds.length > 1;
  const label = (word, id) => {
    const who = byId.get(id);
    return several ? `${w(word)}: ${who?.firstName || who?.name || ''}` : w(word);
  };
  return (
    <div className={s.cancelLinks}>
      {moves.map((id) => (
        <button key={`r:${id}`} type="button" className={s.rescheduleLink} onClick={() => onReschedule({ lessonId: lesson.id, studentId: id })}>
          {label('reschedule', id)}
        </button>
      ))}
      {cancels.map((id) => (
        <button key={`c:${id}`} type="button" className={s.cancelLink} onClick={() => onCancel({ lessonId: lesson.id, studentId: id })}>
          {label('cancelBooking', id)}
        </button>
      ))}
    </div>
  );
}

// Refusals that mean the page is out of date: look again.
const STALE = ['cancel_changed', 'not_on_lesson', 'cancel_started', 'cancel_recorded', 'cancel_off', 'no_student'];

export default function CancelBooking({
  view, choice, lesson, studentId, onClose, onCancelled,
}) {
  const offer = lesson.cancel?.[studentId] || null;
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [done, setDone] = useState(false);
  const locale = view.studio.locale || undefined;
  const zone = view.family.timeZone;
  const who = view.students.find((x) => x.id === studentId);
  const max = familyPortalCancelNoteMax();

  const clock = (date, time) => {
    if (!time) return null;
    const label = zone ? zoneAbbreviation(zone, date, time, locale || 'en-US') : '';
    return `${formatTime(time, locale)}${label ? ` ${label}` : ''}`;
  };

  const facts = (
    <div className={b.facts}>
      <span className={b.fact}>{who?.name || who?.firstName || ''}</span>
      <span className={b.fact}>{formatLongDate(lesson.date, locale)}</span>
      {lesson.startTime ? <span className={b.fact}>{clock(lesson.date, lesson.startTime)}</span> : null}
      {lesson.category ? <span className={b.fact}>{lesson.category}</span> : null}
    </div>
  );

  if (done) {
    return (
      <section className={p.panel}>
        <h2 className={b.complete}>{w('cancelDone')}</h2>
        {facts}
        <div className={b.actions}><Button onClick={onCancelled}>{w('done')}</Button></div>
      </section>
    );
  }

  if (!offer) {
    return (
      <section className={p.panel}>
        <p className={p.body}>{familyPortalRefusal('cancel_unavailable')}</p>
        <div className={b.actions}><Button variant="secondary" onClick={onClose}>{w('cancel')}</Button></div>
      </section>
    );
  }

  const yes = () => {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    cancelFamilyPortalLesson({
      studioUid: choice.studioUid,
      familyId: choice.familyId,
      studentId,
      occurrenceId: lesson.id,
      expected: offer.code,
      note: note.trim() || null,
    }).then(
      () => { setBusy(false); setDone(true); },
      (err) => {
        setBusy(false);
        setFailure({ message: err?.message || familyPortalRefusal('failed'), refusal: err?.refusal || null });
      },
    );
  };

  const stale = STALE.includes(failure?.refusal);
  const used = [...note].length;

  return (
    <section className={p.panel}>
      <h2 className={p.panelTitle}>{w('cancelBooking')}</h2>
      {facts}
      {view.studio.policyText ? (
        <div className={s.policy}>
          <span className={s.policyTitle}>{w('cancelPolicy')}</span>
          {view.studio.policyText}
        </div>
      ) : null}
      <p className={s.effect}>
        {offer.side === CANCEL_SIDES.before ? w('cancelBeforeDeadline') : w('cancelAfterDeadline')}
        {offer.deadline ? ` ${w('cancelDeadline')}: ${formatLongDate(offer.deadline.date, locale)} ${clock(offer.deadline.date, offer.deadline.time) || ''}.` : ''}
      </p>
      <p className={s.effect}>{offer.text}</p>
      <p className={s.confirm}>{w('cancelConfirm')}</p>
      <div className={p.form}>
        <label className={p.label} htmlFor="portal-cancel-note">{w('cancelNote')}</label>
        <textarea
          id="portal-cancel-note"
          className={s.note}
          value={note}
          maxLength={max}
          disabled={busy}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className={s.noteFoot}>
          <span>{w('cancelNoteOptional')}</span>
          <span>{`${used} / ${max}`}</span>
        </div>
      </div>
      {failure ? <p className={p.error} role="alert">{failure.message}</p> : null}
      <div className={b.actions}>
        {stale ? (
          <Button variant="secondary" onClick={onCancelled}>{w('retry')}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>{w('cancelNo')}</Button>
            <Button variant="danger" onClick={yes} loading={busy}>{busy ? w('cancelling') : w('cancelYes')}</Button>
          </>
        )}
      </div>
    </section>
  );
}
